import React, { useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import CheckToggleField from 'components/CheckToggleField';
import TextAreaField from 'components/TextAreaField';
import useCallbackAsync from 'hooks/useCallbackAsync';
import SelectLeads from '../SelectLeads';
import api from 'api';
import { BuyerEnquiryHistory } from 'models/DealRoom';
import { getCheckedValuesInObject } from 'utils';
import ListingContext from 'router/subrouters/Dash/subrouters/private/subrouters/Portfolio/subrouters/Seller/subrouters/ListingDetailSeller/providers/Listing/Listing.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';

import './InviteLeadsForm.styles.scss';

type FormValues = {
  leads: Record<string, boolean>;
  includeMessage?: boolean;
  customMessage?: string;
};

type InviteLeadsFormProps = {
  className?: string;
  onClose: () => void;
};

const InviteLeadsForm: React.FC<InviteLeadsFormProps> = (props) => {
  const { className, onClose } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [leads, setLeads] = useState<BuyerEnquiryHistory[]>([]);

  const {
    listing: { _id: listingId },
    dealRoom: { _id: dealRoomId, name },
    setEnquiry,
  } = useContext(ListingContext);

  const { popSuccess } = useContext(PopNotificationsContext);

  const classes = classNames('invite-leads-form', className);

  const { t } = useTranslation();

  const [onSubmit] = useCallbackAsync(async (values: FormValues) => {
    const { leads, includeMessage, customMessage } = values;

    const buyerIds = getCheckedValuesInObject(leads);

    const { data } = await api.portfolio.seller.addPreviousLead(listingId, {
      buyerIds,
      ...(includeMessage && customMessage && { customMessage }),
    });

    popSuccess(t('successfullyInvitedLeads', { count: buyerIds.length }));
    setEnquiry(data);
    onClose();
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.dealRoom.getPreviousDealsLeads(dealRoomId);
        setLeads(data);
      } catch {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dealRoomId]);

  if (isLoading) return <Loader />;

  if (!isLoading && !leads.length) return <p>{t('noLeadsFromDeal')}</p>;

  return (
    <Form
      onSubmit={onSubmit}
      render={({
        submitting,
        invalid,
        values: { includeMessage },
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {submitting && <Loader />}

          <p>{t('inviteLeadsFromDealsDescription', { dealRoomName: name })}</p>

          <SelectLeads leads={leads} />

          <Field
            name="includeMessage"
            type="checkbox"
            label={t('includeMessage')}
            component={CheckToggleField}
          />

          {includeMessage && (
            <Field name="customMessage" component={TextAreaField} rows={3} />
          )}

          <div className="actions">
            <Button variant="link" onClick={onClose}>
              {t('cancel')}
            </Button>

            <Button type="submit" disabled={submitting || invalid}>
              {t('addEnquiries')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default InviteLeadsForm;
